import type * as ngRouter from '@uirouter/angularjs';
import * as angular from 'angular';
import { Observable } from 'rxjs';
import { combineLatest, map, startWith, takeUntil } from 'rxjs/operators';

import { ChartState } from '@mode/legacy/types-app-state';
import { forceResize, FLAMINGO_RESERVED_REFS } from '@mode/shared/util-js';
import { ToastService } from '@mode/shared/util-toast';
import { ActiveExplorationActionType } from '~/actions/exploration/active-exploration.actions';
import { MixableBaseController } from '~/common/component';
import { ExplorationVizService } from '~/services/exploration/exploration-viz.service';
import { FlamingoService } from '~/services/flamingo-service';
import { ReportApiService } from '~/services/report-api-service';
import { AppStore } from '~/types/main.types';
import { ExplorationObservables } from '~/types/viz-designer.types';

import { EncFilterField, Encoding } from '@mode-vegas/vegas';
import { ChartFacade } from '@mode/charts/contract';
import { AvatarTypes, ChartTypes, FlamingoTypes, InterActions, ReportTypes } from '@mode/shared/contract-common';
import { DataViewFacade, FlamingoFacade } from '@mode/switch-views/contract';
import { makeExplorationObservables } from '~/observables/exploration.observables';

class ExplorationViewerController extends MixableBaseController {
  static $inject = [
    '$window',
    'RxStoreService',
    'FlamingoService',
    'ToastService',
    'ExplorationVizService',
    'ReportApiService',
    'DataViewFacade',
    'ChartFacade',
    'FlamingoFacade',
    '$state',
  ];

  // Observables
  private observables: ExplorationObservables;
  private queryToken$: Observable<string>;
  private vizToken$: Observable<string>;
  private chartState$: Observable<ChartState>;
  private usedParameters$: Observable<ReportTypes.ReportRunFormField[]>;
  private explorationQueryExists$: Observable<boolean>;
  private isViewLoaded$: Observable<boolean>;
  private description$: Observable<string>;
  private encoding$: Observable<Encoding>;
  private activeFilters$: Observable<ReadonlyArray<Readonly<EncFilterField>>>;
  private view$: Observable<FlamingoTypes.ModeView>;

  private showResults$: Observable<boolean>;
  private planLoading$: Observable<boolean>;
  private invalid$: Observable<boolean>;

  private isChart$: Observable<boolean>;
  private isTable$: Observable<boolean>;
  private isBigValue$: Observable<boolean>;
  private canUndo$: Observable<boolean>;
  private canRedo$: Observable<boolean>;

  // Local state
  private mini = false;
  private sidePanelTitle = 'details';
  private formattedAvatarURLStyle$: Observable<string>;
  private readOnly: boolean;
  private runIdUrlParam: string;
  private inputName: string;
  private objectContext = ChartTypes.ObjectType.Exploration;

  public focusLink: string;

  constructor(
    private $window: angular.IWindowService,
    private rxStore: AppStore,
    private flamingoService: FlamingoService, // for side effects
    private alertService: ToastService,
    private explorationVizService: ExplorationVizService,
    private reportApiService: ReportApiService,
    private dataViewFacade: DataViewFacade, // @TODO: (@anthonysimone) [GF] - temporary until active-exploration is migrated to angular and we implement an active-exploration facade that compiles the separate apis
    private chartFacade: ChartFacade,
    private flamingoFacade: FlamingoFacade,
    private $state: ngRouter.StateService
  ) {
    super();

    this.readOnly = true;
  }

  $onInit() {
    this.observables = makeExplorationObservables(
      this.rxStore,
      this.dataViewFacade,
      this.chartFacade,
      this.flamingoFacade
    );

    this.queryToken$ = this.observables.queryToken$;
    this.chartState$ = this.observables.chartState$;
    this.planLoading$ = this.observables.planLoading$;
    this.vizToken$ = this.observables.token$;
    this.usedParameters$ = this.observables.usedParameters$;
    this.explorationQueryExists$ = this.observables.explorationQueryExists$;
    this.isViewLoaded$ = this.observables.isViewLoaded$;
    this.description$ = this.observables.description$;
    this.encoding$ = this.observables.encoding$;
    this.activeFilters$ = this.observables.activeFilters$;
    this.view$ = this.observables.view$;

    // Derived observables
    this.isChart$ = this.observables.chartType$.pipe(map((type) => type !== 'table' && type !== 'bigValue'));
    this.isTable$ = this.observables.chartType$.pipe(map((type) => type === 'table'));
    this.isBigValue$ = this.observables.chartType$.pipe(map((type) => type === 'bigValue'));
    this.canUndo$ = this.chartState$.pipe(map((chartState) => chartState.canUndo));
    this.canRedo$ = this.chartState$.pipe(map((chartState) => chartState.canRedo));
    this.formattedAvatarURLStyle$ = this.observables.explorationOwnerAvatar$.pipe(
      map((avatar: AvatarTypes.Avatar) => `url('${avatar.href}')`)
    ); // This will construct the appropriate dynamic style needed to drive the users avatar image

    // Build 2 designer only observables
    this.showResults$ = this.observables.canRender$.pipe(
      combineLatest(this.observables.planSucceeded$.pipe(startWith(false))),
      map(([canRender, success]) => canRender && success)
    );

    this.invalid$ = this.observables.canRender$.pipe(
      combineLatest(this.observables.planFailed$.pipe(startWith(false))),
      map(([canRender, failed]) => !canRender || failed)
    );

    this.focusLink = this.$window.location.href;
    // End observable init

    // Report API Service embed handlers - Update on chart interactions (granularity, view data, etc)
    this.reportApiService
      .makeObservable(this.observables.token$)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(({ action, chartToken }) => {
        if (action.type === InterActions.Type.ContextClick) {
          switch (action.item.type) {
            case InterActions.ContextItemType.ViewData:
              this.rxStore.dispatch({
                type: ActiveExplorationActionType.ViewChartData,
                target: action.item.target,
                chartToken: FLAMINGO_RESERVED_REFS.Exploration,
              });
              break;
          }
        }
      });
  }

  // This will queue a resize event trigger for the browser to consume post paint sequence completion.
  toggleMiniState() {
    this.mini = !this.mini;
    forceResize();
  }

  /**
   * Go back in state history.
   */
  undo(vizToken: string) {
    this.explorationVizService.undo(vizToken);
  }

  /**
   * Go forward in state history.
   */
  redo(vizToken: string) {
    this.explorationVizService.redo(vizToken);
  }

  /**
   * Copy success.
   */
  copySuccess() {
    this.alertService.success('Link to Exploration copied successfully');
  }
}

export const ID = 'explorationViewer';

export const ExplorationViewerComponent: angular.IComponentOptions = {
  template: `
    <parameters-info
      ng-if="(ctrl.usedParameters$ | async:this).length > 0"
      parameters="ctrl.usedParameters$ | async:this"
      report-url="ctrl.$state.href('reportShow.noRuns.dashboard', { account: ctrl.$state.params.account, reportId: ctrl.$state.params.reportId, });"
    ></parameters-info>
    <div class="exploration-layout-container" ng-if="(ctrl.explorationQueryExists$ | async:this) === true && (ctrl.isViewLoaded$ | async:this) === true">
      <div class="exploration-viewer" ng-class="{'has-params': (ctrl.usedParameters$ | async:this).length > 0}">
        <div class="new-viz-actions">
          <div class="ds-flex-row">
            <button class="new-viz-action action-left hide-2 plus-plan-only"
                ng-click="ctrl.undo((ctrl.vizToken$ | async:this))"
                ng-class="{ disabled: !(ctrl.canUndo$ | async:this) }"
                ng-disabled="!(ctrl.canUndo$ | async:this)">
              <svg-icon type="'back'" uib-tooltip="Undo"></svg-icon>
            </button>
            <button class="new-viz-action action-right hide-2 plus-plan-only"
                ng-click="ctrl.redo((ctrl.vizToken$ | async:this))"
                ng-class="{ disabled: !(ctrl.canRedo$ | async:this) }"
                ng-disabled="!(ctrl.canRedo$ | async:this)">
              <svg-icon type="'forward'" uib-tooltip="Redo"></svg-icon>
            </button>
          </div>
        </div>
        <div class="designer-container">
          <designer-renderer
            ng-if="(ctrl.isChart$ | async:this) || (ctrl.isTable$ | async:this)"
            read-only="ctrl.readOnly"
            observables="ctrl.observables"
            chart-service="ctrl.explorationVizService"
            is-view-page="true"
            object-context="ctrl.objectContext"
          ></designer-renderer>
          <big-value-designer-renderer
            ng-if="ctrl.isBigValue$ | async:this"
            read-only="ctrl.readOnly"
            observables="ctrl.observables"
            chart-service="ctrl.explorationVizService"
            is-view-page="true"
            object-context="ctrl.objectContext"
          ></big-value-designer-renderer>
        </div>
      </div>
      <div class="ds-side-panel-area right-side" ng-class="{'mini-state': ctrl.mini}">
        <div class="ds-side-panel full-height-state">
          <div class="ds-side-panel-header gray-state">
            <p ng-if="!ctrl.mini" class="ds-side-panel-typography header-title-state">{{ ctrl.sidePanelTitle }}</p>
            <button class="ds-side-panel-toggle-container" ng-click="ctrl.toggleMiniState()">
              <span class="ds-side-panel-toggle-item" ng-class="{'off-state': ctrl.mini}">
                <svg class="arrow-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                  <path d="M5.88 4.12L13.76 12l-7.88 7.88L8 22l10-10L8 2z" fill="currentColor"/>
                  <path d="M0 0h24v24H0z" fill="none"/>
                </svg>
              </span>
            </button>
          </div>
          <div class="ds-side-panel-body">
            <p ng-if="ctrl.mini" class="ds-side-panel-typography header-title-state vertical">{{ ctrl.sidePanelTitle }}</p>
            <div ng-if="!ctrl.mini" class="ds-side-panel-section overflow-panel">
              <div class="ds-side-panel-avatar-container" ng-switch="(ctrl.observables.explorationOwnerAvatar$ | async:this).type">
                <div ng-switch-when="initials" class="ds-side-avatar mode-avatar-initials" ng-class="[(ctrl.observables.explorationOwnerAvatar$ | async:this).color_class]">
                  {{ (ctrl.observables.explorationOwnerAvatar$ | async:this).initials }}
                </div>
                <div
                  ng-switch-default
                  class="ds-side-avatar image-state"
                  ng-style="{'background-image': (ctrl.formattedAvatarURLStyle$ | async:this)}"></div>
                <div>
                  <p class="ds-side-panel-typography title-state">{{ ctrl.observables.explorationOwnerName$ | async:this }}</p>
                  <p class="ds-side-panel-typography">Edited {{ ctrl.observables.explorationUpdatedAt$ | async:this }}</p>
                </div>
              </div>
              <div class="ds-side-panel-label-value-container">
                <p class="ds-side-panel-typography label-state">Exploration</p>
                <p class="ds-side-panel-typography">{{ ctrl.observables.name$ | async:this }}</p>
              </div>
              <div class="ds-side-panel-label-value-container">
                <p class="ds-side-panel-typography label-state">Source Report</p>
                <p class="ds-side-panel-typography">
                  <a ui-sref="reportShow.noRuns.dashboard({ account: ctrl.$state.params.account, reportId: ctrl.$state.params.reportId })" class="ds-side-panel-link">
                    {{ ctrl.observables.reportDisplayName$ | async:this }}
                  </a>
                </p>
              </div>
              <div ng-if="!(ctrl.description$ | async:this)" class="ds-side-panel-label-value-container">
                <p class="ds-side-panel-typography label-state">Description</p>
                <p class="ds-side-panel-typography value-state">None</p>
              </div>
              <div ng-if="ctrl.description$ | async:this" class="ds-side-panel-full-width-label-value">
                <p class="ds-side-panel-typography label-state">Description</p>
                <p class="ds-side-panel-typography value-state show-whitespace">{{ ctrl.description$ | async:this }}</p>
              </div>
            </div>
            <div ng-if="!ctrl.mini" class="ds-side-panel-section">
              <div class="ds-side-panel-label-value-container">
                <div class="ds-flex-row center-state">
                  <p class="ds-side-panel-typography label-state centered-state">
                    Data
                  </p>
                </div>
                <div class="ds-flex-row center-state">
                  <p class="ds-side-panel-typography centered-state link-state dataset-link-column">
                    <dataset-link query-token="ctrl.observables.queryToken$ | async:this"
                      query-name="ctrl.observables.explorationQueryName$ | async:this"
                      account-token="ctrl.$state.params.account"
                      report-token="ctrl.$state.params.reportId">
                    </dataset-link>
                  </p>
                </div>
              </div>
              <div class="ds-side-panel-label-value-container">
                <p class="ds-side-panel-typography label-state">Last ran</p>
                <p class="ds-side-panel-typography value-state">{{ ctrl.observables.queryCompletedAt$ | async:this }}</p>
              </div>
              <div class="ds-side-panel-label-value-container">
                <p class="ds-side-panel-typography label-state">Refreshes</p>
                <p class="ds-side-panel-typography value-state">automatically</p>
              </div>
              <div ng-if="(ctrl.activeFilters$ | async:this).length > 0" class="ds-side-panel-label-value-container">
                <p class="ds-flex-row ds-side-panel-typography center-state label-state">Filters</p>
                <p class="ds-flex-row ds-side-panel-typography center-state value-state">
                  {{(ctrl.activeFilters$ | async:this).length}} applied
                </p>
              </div>
            </div>
            <div ng-if="!ctrl.mini && (ctrl.activeFilters$ | async:this).length > 0" class="ds-side-panel-section overflow-panel --inset">
              <filters-info class="ds-side-panel-typography" view="(ctrl.view$ | async:this)" filters="(ctrl.activeFilters$ | async:this)"></filters-info>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- TODO: re-enable when a consistent loading state is available-->
    <div
      ng-if="(ctrl.explorationQueryExists$ | async:this) === false"
      class="exploration-error-container"
    >
      <mode-simple-error legacy-icon="icon-broken-viz">
        Sorry, this exploration can't be displayed. The underlying dataset has been deleted.
      </mode-simple-error>
    </div>
  `,
  controller: ExplorationViewerController,
  controllerAs: 'ctrl',
};
